import { GpsType } from '../../../../global/components/layout/layout-models';
import { TerminationProps } from '../../models/arc-epi-props';
import { Employment } from '../employment/employment-model';
import { TerminationConfirmationPageContentModel, TerminationTypes } from './models/employment-termination-model';
import { EmploymentCombinationTypes } from './models/termination-form-model';

const TerminationApiDateUnderReview = 'PATCH_TERMINATION_TERMINATION_DATE_UNDER_REVIEW';
const TerminationApiCircumstancesUnderReview = 'PATCH_TERMINATION_EMPLOYMENT_CIRCUMSTANCES_UNDER_REVIEW';

export function getEmploymentTypeCombo(stipEmployment: Employment, nonStipEmployment: Employment) {
    if (!stipEmployment && !nonStipEmployment) return null;

    if (stipEmployment && nonStipEmployment) {
        if (stipEmployment.termination_date && nonStipEmployment.termination_date) {
            return EmploymentCombinationTypes.Terminated;
        }

        if (stipEmployment.termination_date) {
            return EmploymentCombinationTypes.NonStipendiary;
        }

        if (nonStipEmployment.termination_date) {
            return EmploymentCombinationTypes.Stipendiary;
        }

        return EmploymentCombinationTypes.StipAndNonStip;
    }

    if (nonStipEmployment) {
        return !nonStipEmployment.termination_date ? EmploymentCombinationTypes.NonStipendiary : EmploymentCombinationTypes.Terminated;
    }

    return !stipEmployment.termination_date ? EmploymentCombinationTypes.Stipendiary : EmploymentCombinationTypes.Terminated;
}

export function getInitialEmploymentTypes(employmentTypeCombo: EmploymentCombinationTypes) {

    if (employmentTypeCombo === EmploymentCombinationTypes.Stipendiary) return [EmploymentCombinationTypes.Stipendiary];

    if (employmentTypeCombo === EmploymentCombinationTypes.NonStipendiary) return [EmploymentCombinationTypes.NonStipendiary];

    return [];
}

function GetEmploymentTerminatedDescription(terminationProps: TerminationProps, isStipendiary: boolean, isNonStipendiary: boolean, apiMessages: string[]) : string {
    const isDateUnderReview = apiMessages?.includes(TerminationApiDateUnderReview);
    const isCircumstancesUnderReview = apiMessages?.includes(TerminationApiCircumstancesUnderReview);

    if (isStipendiary && isNonStipendiary && isDateUnderReview){
        return terminationProps?.stipNonStipTerminatedEmployeeDateUnderReviewDescription;
    }

    if (isStipendiary && isNonStipendiary && isCircumstancesUnderReview){
        return terminationProps?.stipNonStipTerminatedEmployeeCircumstancesUnderReviewDescription;
    }

    if (isStipendiary && isNonStipendiary){
        return terminationProps?.stipNonStipTerminatedEmployeeDescription;
    }

    if (isStipendiary && isDateUnderReview){
        return terminationProps?.stipTerminatedEmployeeDateUnderReviewDescription;
    }

    if (isStipendiary && isCircumstancesUnderReview){
        return terminationProps?.stipTerminatedEmployeeCircumstancesUnderReviewDescription;
    }

    if (isNonStipendiary && isDateUnderReview){
        return terminationProps?.nonStipTerminatedEmployeeDateUnderReviewDescription;
    }

    if (isNonStipendiary && isCircumstancesUnderReview){
        return terminationProps?.nonStipTerminatedEmployeeCircumstancesUnderReviewDescription;
    }

    return isStipendiary ? terminationProps?.stipTerminatedEmployeeDescription : terminationProps?.nonStipTerminatedEmployeeDescription;
}

export function getTerminationConfirmationContent(terminationProps: TerminationProps, terminationType: TerminationTypes, employmentTypes: string[], apiMessages: string[]): TerminationConfirmationPageContentModel {

    if (!terminationProps || !terminationType || !employmentTypes) return {} as TerminationConfirmationPageContentModel;

    switch (terminationType) {
        case TerminationTypes.Employment:
            if (employmentTypes?.includes(EmploymentCombinationTypes.Stipendiary) && employmentTypes?.includes(EmploymentCombinationTypes.NonStipendiary)) {
                return {
                    pageTitle: terminationProps?.stipNonStipConfirmationTitle,
                    terminatedEmployeeHeader: terminationProps?.stipNonStipTerminatedEmployeeHeader,
                    terminatedEmployeeDescription: GetEmploymentTerminatedDescription(terminationProps, true, true, apiMessages),
                    nextStepsHeader: terminationProps?.stipNonStipNextStepsHeader,
                    groupHealthLifeMessage: terminationProps?.stipNonStipGroupHealthLifeMessage,
                    terminateBenefitsLinksText: terminationProps?.stipNonStipTerminateBenefitsLinkText,
                    nonGroupAdminNextStepsMessage: terminationProps?.stipNonStipNonGroupAdminNextStepsMessage,
                };
            }
            if (employmentTypes?.includes(EmploymentCombinationTypes.Stipendiary)) {
                return {
                    pageTitle: terminationProps?.stipConfirmationTitle,
                    terminatedEmployeeHeader: terminationProps?.stipTerminatedEmployeeHeader,
                    terminatedEmployeeDescription: GetEmploymentTerminatedDescription(terminationProps, true, false, apiMessages),
                    nextStepsHeader: terminationProps?.stipNextStepsHeader,
                    groupHealthLifeMessage: terminationProps?.stipGroupHealthLifeMessage,
                    terminateBenefitsLinksText: terminationProps?.stipTerminateBenefitsLinkText,
                    nonGroupAdminNextStepsMessage: terminationProps?.stipNonGroupAdminNextStepsMessage,
                };
            }

            if (employmentTypes?.includes(EmploymentCombinationTypes.NonStipendiary)) {
                return {
                    pageTitle: terminationProps?.nonStipConfirmationTitle,
                    terminatedEmployeeHeader: terminationProps?.nonStipTerminatedEmployeeHeader,
                    terminatedEmployeeDescription: GetEmploymentTerminatedDescription(terminationProps, false, true, apiMessages),
                    nextStepsHeader: terminationProps?.nonStipNextStepsHeader,
                    groupHealthLifeMessage: terminationProps?.nonStipGroupHealthLifeMessage,
                    terminateBenefitsLinksText: terminationProps?.nonStipTerminateBenefitsLinkText,
                    nonGroupAdminNextStepsMessage: terminationProps?.nonStipNonGroupAdminNextStepsMessage,
                };
            }
            return {} as TerminationConfirmationPageContentModel;
        case TerminationTypes.Death:
            return {
                pageTitle: terminationProps?.deathConfirmationTitle,
                terminatedEmployeeHeader: terminationProps?.deathTerminatedEmployeeHeader,
                terminatedEmployeeDescription: terminationProps?.deathTerminatedEmployeeDescription,
                nextStepsHeader: '',
                groupHealthLifeMessage: terminationProps?.deathBottomMessage,
                terminateBenefitsLinksText: '',
                nonGroupAdminNextStepsMessage: terminationProps?.deathBottomMessage,
            };
        case TerminationTypes.NeverWorkedHere:
            return {
                pageTitle: terminationProps?.neverWorkedHereConfirmationTitle,
                terminatedEmployeeHeader: terminationProps?.neverWorkedHereTerminatedEmployeeHeader,
                terminatedEmployeeDescription: '',
                nextStepsHeader:'',
                groupHealthLifeMessage: '',
                terminateBenefitsLinksText:'',
                nonGroupAdminNextStepsMessage: '',
                loaText: terminationProps?.neverWorkedHereEffectiveDescription,

            };

        case TerminationTypes.LeaveOfAbsence:
            return {
                pageTitle: terminationProps?.loaConfirmationTitle,
                terminatedEmployeeHeader: terminationProps?.loaTerminatedEmployeeHeader,
                terminatedEmployeeDescription: terminationProps?.loaTerminatedEmployeeDescription,
                nextStepsHeader: terminationProps?.loaNextStepsHeader,
                groupHealthLifeMessage: terminationProps?.loaGroupHealthLifeMessage,
                terminateBenefitsLinksText: terminationProps?.loaTerminateBenefitsLinkText,
                nonGroupAdminNextStepsMessage: terminationProps?.loaGroupAdminNextStepsMessage,
                benefitTerminatedDescription: terminationProps?.loaBenefitTerminatedDescription,
                benefitTerminatedTitle: terminationProps?.benefitTerminatedHeader,
                loaText:terminationProps?.loaEffectiveDescription,
            };


        default:
            return {} as TerminationConfirmationPageContentModel;
    }

}

export function mapErrorToTerminationKey(errorKey: string) {
    switch (errorKey) {
        case 'preliminary_date_of_death':
        case 'start_date':
            return 'termination_date';
        default:
            return errorKey;
    }
}

export function getConfirmationGpsType(terminationType: TerminationTypes, employmentTypes: string[]) {
    switch (terminationType) {
        case TerminationTypes.Employment:
            if (employmentTypes?.includes(EmploymentCombinationTypes.Stipendiary) && employmentTypes?.includes(EmploymentCombinationTypes.NonStipendiary)) {
                return GpsType.TERMINATION_CONFIRMATION_STIP_NONSTIP;
            }
            if (employmentTypes?.includes(EmploymentCombinationTypes.Stipendiary)) {
                return GpsType.TERMINATION_CONFIRMATION_STIP;
            }

            if (employmentTypes?.includes(EmploymentCombinationTypes.NonStipendiary)) {
                return GpsType.TERMINATION_CONFIRMATION_NONSTIP;
            }
            return null;
        case TerminationTypes.Death:
            return GpsType.TERMINATION_CONFIRMATION_DEATH;
        case TerminationTypes.NeverWorkedHere:
            return GpsType.TERMINATION_CONFIRMATION_NEVER_WORKED_HERE;
        default:
            return null;
    }
}